import { useState, useEffect, useMemo } from 'react';
import { getUserRoles } from '@/services/data/roles';
import { getCsmTasks, type CsmTask } from '@/services/data/csmTasks';
import type { CalendarEvent } from '@/services/business/calendar';

/**
 * Extended CalendarEvent type for CSM tasks in the calendar
 */
export interface CsmCalendarEvent extends Omit<CalendarEvent, 'type'> {
  type: 'csm_task';
  csmTaskData: CsmTask;
}

interface CsmCalendarData {
  isCsm: boolean;
  csmTasks: CsmTask[];
  csmEvents: CsmCalendarEvent[];
  isLoading: boolean;
}

/**
 * Convert CsmTask to CalendarEvent-like format
 */
function mapCsmTasksToEvents(tasks: CsmTask[]): CsmCalendarEvent[] {
  return tasks.map(task => {
    const dueDate = new Date(task.due_date + 'T09:00:00');

    return {
      id: `csm-task-${task.id}`,
      date: dueDate,
      type: 'csm_task' as const,
      title: task.title,
      description: task.description || undefined,
      time: '09:00',
      csmTaskData: task,
    };
  });
}

/**
 * Hook to detect if user is CSM and load their tasks as calendar events
 */
export function useCsmCalendarData(userId: string | null): CsmCalendarData {
  const [isCsm, setIsCsm] = useState(false);
  const [csmTasks, setCsmTasks] = useState<CsmTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);

      // Check CSM role or admin/super_admin roles
      const rolesResult = await getUserRoles(userId);
      if (cancelled) return;

      const roles = rolesResult.data || [];
      const canSeeCsmTasks = roles.includes('csm') || roles.includes('admin') || roles.includes('super_admin');

      if (!canSeeCsmTasks) {
        setIsCsm(false);
        setIsLoading(false);
        return;
      }

      setIsCsm(true);

      // Fetch active tasks
      const tasksResult = await getCsmTasks({ status: ['todo', 'in_progress'] });
      if (cancelled) return;

      if (tasksResult.success) {
        setCsmTasks(tasksResult.data);
      }

      setIsLoading(false);
    };

    load();
    return () => { cancelled = true; };
  }, [userId]);

  const csmEvents = useMemo(() => mapCsmTasksToEvents(csmTasks), [csmTasks]);

  return { isCsm, csmTasks, csmEvents, isLoading };
}
